// Referencia: Angular v18 Documentation - Standalone Components, Signals
// Componente de chat lateral para asistentes IA

import { CommonModule } from '@angular/common';
import { Component, computed, input, output, signal, WritableSignal, effect, untracked } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { AiChatType, MOCK_AI_CONVERSATIONS } from '../models/tiktok-ui.model';

@Component({
  selector: 'app-ai-chat-sidebar',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  template: `
    <div class='ai-chat-container'>
      @if (showMenu()) {
        <!-- Menú principal de asistentes -->
        <div class='ai-menu' [@fadeSlide]>
          <div class='ai-menu-header'>
            <h2 class='ai-menu-title'>🤖 wokapIA</h2>
            <p class='ai-menu-subtitle'>Elige tu asistente de reclutamiento inteligente</p>
          </div>
          <div class='ai-menu-options'>
            <button class='ai-menu-option' (click)="selectAssistant('filter')">
              <div class='ai-menu-option__icon'>🔍</div>
              <div class='ai-menu-option__content'>
                <h3 class='ai-menu-option__title'>Filtrar tus candidat{{'@'}}s</h3>
                <p class='ai-menu-option__description'>Filtro Inteligente para filtrar candidat{{'@'}}s de forma avanzada según criterios clave.</p>
              </div>
              <i class='pi pi-chevron-right ai-menu-option__arrow'></i>
            </button>

            <button class='ai-menu-option' (click)="selectAssistant('search')">
              <div class='ai-menu-option__icon'>🎯</div>
              <div class='ai-menu-option__content'>
                <h3 class='ai-menu-option__title'>Buscar candidat{{'@'}}s</h3>
                <p class='ai-menu-option__description'>Búsqueda Avanzada para encontrar candidat{{'@'}}s específicos con búsqueda semántica.</p>
              </div>
              <i class='pi pi-chevron-right ai-menu-option__arrow'></i>
            </button>

            <button class='ai-menu-option' (click)="selectAssistant('killerQuestions')">
              <div class='ai-menu-option__icon'>❓</div>
              <div class='ai-menu-option__content'>
                <h3 class='ai-menu-option__title'>Killer Questions</h3>
                <p class='ai-menu-option__description'>Killer Questions AI genera preguntas clave para evaluar mejor a l{{'@'}}s candidat{{'@'}}s.</p>
              </div>
              <i class='pi pi-chevron-right ai-menu-option__arrow'></i>
            </button>

            <button class='ai-menu-option' (click)="selectAssistant('navigate')">
              <div class='ai-menu-option__icon'>🧭</div>
              <div class='ai-menu-option__content'>
                <h3 class='ai-menu-option__title'>Navega por wokap</h3>
                <p class='ai-menu-option__description'>Explora y descubre candidat{{'@'}}s de distintas áreas y perfiles.</p>
              </div>
              <i class='pi pi-chevron-right ai-menu-option__arrow'></i>
            </button>

            <button class='ai-menu-option' (click)="selectAssistant('suggested')">
              <div class='ai-menu-option__icon'>✨</div>
              <div class='ai-menu-option__content'>
                <h3 class='ai-menu-option__title'>Candidat{{'@'}}s sugerid{{'@'}}s</h3>
                <p class='ai-menu-option__description'>Recomendaciones IA que sugieren candidat{{'@'}}s ideales de forma automática.</p>
              </div>
              <i class='pi pi-chevron-right ai-menu-option__arrow'></i>
            </button>
          </div>
        </div>
      } @else {
        <!-- Chat del asistente seleccionado -->
        <div class='ai-chat-view' [@fadeSlide]>
          <!-- Header con botón de retorno -->
          <div class='ai-chat-header'>
            <p-button
              icon='pi pi-arrow-left'
              [text]='true'
              [rounded]='true'
              severity='secondary'
              (onClick)='returnToMenu()'
              styleClass='ai-back-button' />
            <div class='ai-chat-header__avatar'>{{ assistantConfig().avatar }}</div>
            <div class='ai-chat-header__info'>
              <div class='ai-chat-header__name'>{{ assistantConfig().name }}</div>
              <div class='ai-chat-header__description'>{{ assistantConfig().description }}</div>
            </div>
          </div>

          <!-- Mensajes del chat -->
          <div class='ai-chat-messages'>
            @for (message of assistantConfig().messages; track $index) {
              <div class='ai-message' [class.ai-message--user]="message.role === 'user'" [class.ai-message--assistant]="message.role === 'assistant'">
                @if (message.role === 'assistant') {
                  <div class='ai-message__avatar'>{{ assistantConfig().avatar }}</div>
                }
                <div class='ai-message__content'>
                  <div class='ai-message__bubble'>
                    <div class='ai-message__text'>{{ message.content }}</div>
                  </div>
                  <div class='ai-message__timestamp'>
                    {{ message.timestamp | date:'short' }}
                  </div>
                </div>
                @if (message.role === 'user') {
                  <div class='ai-message__avatar ai-message__avatar--user'>👤</div>
                }
              </div>
            }
          </div>

          <!-- Footer informativo -->
          <div class='ai-chat-footer'>
            <div class='ai-chat-footer__notice'>
              💡 Esta es una conversación de ejemplo. Próximamente podrás interactuar en tiempo real.
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .ai-chat-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      background-color: transparent;
    }

    // === Menú de asistentes ===
    .ai-menu {
      display: flex;
      flex-direction: column;
      height: 100%;
      animation: fadeSlide 0.3s ease-out;
    }

    .ai-menu-header {
      padding: 1.5rem;
      text-align: center;
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%);
      border-bottom: 1px solid var(--border-color);
      border-radius: 0.5rem;
      margin-bottom: 1rem;
    }

    .ai-menu-title {
      font-size: 1.5rem;
      font-weight: 700;
      /* Texto blanco para mejorar contraste en el menú inicial */
      color: #ffffff !important;
      margin: 0 0 0.5rem 0;
    }

    .ai-menu-subtitle {
      font-size: 0.875rem;
      color: rgba(255, 255, 255, 0.9) !important;
      margin: 0;
    }

    .ai-menu-options {
      flex: 1;
      overflow-y: auto;
      padding: 0 1rem 1rem 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .ai-menu-option {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem;
      background-color: rgba(59, 130, 246, 0.1);
      border: 2px solid rgba(59, 130, 246, 0.2);
      border-radius: 0.75rem;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: left;
      width: 100%;

      &:hover {
        background-color: rgba(59, 130, 246, 0.2);
        border-color: rgba(59, 130, 246, 0.4);
        transform: translateX(4px);

        .ai-menu-option__arrow {
          transform: translateX(4px);
        }
      }

      &__icon {
        font-size: 2rem;
        flex-shrink: 0;
        color: #ffffff !important;
      }

      &__content {
        flex: 1;
        min-width: 0;
      }

      &__title {
        font-size: 1rem;
        font-weight: 600;
        /* Fuerza color blanco para visibilidad en el menú inicial */
        color: #ffffff !important;
        margin: 0 0 0.25rem 0;
      }

      &__description {
        font-size: 0.8rem;
        color: rgba(255, 255, 255, 0.9) !important;
        margin: 0;
        line-height: 1.4;
      }

      &__arrow {
        font-size: 1rem;
        color: rgba(255, 255, 255, 0.9) !important;
        flex-shrink: 0;
        transition: transform 0.2s ease;
      }
    }

    // === Vista de chat ===
    .ai-chat-view {
      display: flex;
      flex-direction: column;
      height: 100%;
      animation: fadeSlide 0.3s ease-out;
    }

    // Header del asistente
    .ai-chat-header {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1.25rem;
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%);
      border-bottom: 1px solid var(--border-color);
      border-radius: 0.5rem;
      margin-bottom: 1rem;

      ::ng-deep .ai-back-button {
        margin-right: 0.5rem;
        color: #ffffff !important;

        .p-button-icon {
          color: #ffffff !important;
        }
      }

      &__avatar {
        font-size: 2.5rem;
        width: 3.5rem;
        height: 3.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
        flex-shrink: 0;
      }

      &__info {
        flex: 1;
        min-width: 0;
      }

      &__name {
        font-size: 1.1rem;
        font-weight: 700;
        color: var(--text-primary);
        margin-bottom: 0.25rem;
      }

      &__description {
        font-size: 0.8rem;
        color: var(--text-secondary);
        line-height: 1.3;
      }
    }

    // Contenedor de mensajes
    .ai-chat-messages {
      flex: 1;
      overflow-y: auto;
      padding: 0 1rem 1rem 1rem;
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    // Mensaje individual
    .ai-message {
      display: flex;
      gap: 0.75rem;
      align-items: flex-start;

      &--user {
        flex-direction: row-reverse;
      }

      &__avatar {
        font-size: 1.5rem;
        width: 2.5rem;
        height: 2.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        border-radius: 50%;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

        &--user {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
        }
      }

      &__content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
        min-width: 0;
      }

      &__bubble {
        padding: 0.875rem 1.125rem;
        border-radius: 1rem;
        background-color: rgba(59, 130, 246, 0.15);
        border: 1px solid rgba(59, 130, 246, 0.2);
        max-width: 85%;
        word-wrap: break-word;
      }

      &--user &__bubble {
        background-color: rgba(16, 185, 129, 0.15);
        border: 1px solid rgba(16, 185, 129, 0.2);
        margin-left: auto;
        border-radius: 1rem 1rem 0.25rem 1rem;
      }

      &--assistant &__bubble {
        border-radius: 1rem 1rem 1rem 0.25rem;
      }

      &__text {
        font-size: 0.875rem;
        line-height: 1.5;
        color: var(--text-primary);
        white-space: pre-wrap;
      }

      &__timestamp {
        font-size: 0.7rem;
        color: var(--text-secondary);
        opacity: 0.7;
        padding: 0 0.5rem;
      }

      &--user &__timestamp {
        text-align: right;
      }
    }

    // Footer con aviso
    .ai-chat-footer {
      padding: 1rem;
      border-top: 1px solid var(--border-color);
      background-color: rgba(0, 0, 0, 0.2);

      &__notice {
        font-size: 0.75rem;
        color: var(--text-secondary);
        text-align: center;
        line-height: 1.4;
        padding: 0.75rem;
        background-color: rgba(59, 130, 246, 0.1);
        border-radius: 0.5rem;
        border: 1px solid rgba(59, 130, 246, 0.2);
      }
    }

    // Animaciones
    @keyframes fadeSlide {
      from {
        opacity: 0;
        transform: translateX(-10px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    // Scrollbar personalizada
    .ai-chat-messages::-webkit-scrollbar,
    .ai-menu-options::-webkit-scrollbar {
      width: 6px;
    }

    .ai-chat-messages::-webkit-scrollbar-track,
    .ai-menu-options::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 10px;
    }

    .ai-chat-messages::-webkit-scrollbar-thumb,
    .ai-menu-options::-webkit-scrollbar-thumb {
      background: rgba(59, 130, 246, 0.3);
      border-radius: 10px;

      &:hover {
        background: rgba(59, 130, 246, 0.5);
      }
    }
  `],
})
export class AiChatSidebarComponent {
  // Input signal para el tipo de chat
  chatType = input.required<AiChatType>();

  // Output para notificar cambio de asistente
  assistantChange = output<AiChatType>();

  // Signal para controlar si muestra el menú o el chat
  showMenu: WritableSignal<boolean> = signal(true);

  // Computed signal para obtener la configuración del asistente
  assistantConfig = computed(() => MOCK_AI_CONVERSATIONS[this.chatType()]);

  // Ref: docs/libraries/Angular v18 Documentation.txt
  // Reiniciar el menú principal cuando se abre wokapIA desde la vista padre
  // Usamos un approach diferente: observar cuando el componente se vuelve visible
  constructor() {
    effect(() => {
      // Observar cambios en chatType de forma segura
      const type = this.chatType();
      // Usar untracked para leer showMenu sin crear dependencia
      const menuVisible = untracked(() => this.showMenu());

      // Solo resetear cuando el tipo es 'wokapIA' y el menú está oculto
      if (type === 'wokapIA' && !menuVisible) {
        // Usar queueMicrotask para evitar cambios de estado durante la detección de cambios
        queueMicrotask(() => this.showMenu.set(true));
      }
    });
  }

  // Méto-do para seleccionar un asistente
  selectAssistant(type: AiChatType): void {
    this.showMenu.set(false);
    this.assistantChange.emit(type);
  }

  // Méto-do para volver al menú principal
  returnToMenu(): void {
    this.showMenu.set(true);
  }
}
